import { Spinner, Text } from '@chakra-ui/react'
import type { Offer, OfferDiscounts } from 'BrokerTsLib/src/dtos'
import * as global from 'brokertslib/src/helpers/global'
import { useForm, yupResolver } from 'BrokerTsLib/src/rhf'
import { useEffect, useState } from 'react'
import * as yup from 'yup'
import { useGetOffersContext } from '../../../../context/useGetOffersContext'
import { useGetOfferAvailableDiscounts, useSaveOfferDiscounts } from '../../../../hooks/useOffer'
import { EditablePriceCustom } from '../../../EditablePriceCustom'
import { OfferCellWrapper } from './OfferCellWrapper'


const schema = yup.object().shape(
  {
    'discounts': yup.array().of(
      yup.object().shape({
        'discount': yup.number()
          .required('Zadejde výši slevy')
          .when(['discount', 'minDiscount', 'maxDiscount'], ((discount?: number, min?: number, max?: number, schema?: yup.NumberSchema) => {
            if (min) schema = schema?.min(min, 'Sleva není v povoleném rozmezí')
            if (max) schema = schema?.max(max, 'Sleva není v povoleném rozmezí')

            return schema
          }) as any)
      }, [
        // osetreni cyclic dependency vznikajici pri pouziti when... is... then...
        ['discount', 'discount']
      ])
    ),
  }
)

export const DiscountCell = ({ offer, isDisabled }: { offer: Offer, isDisabled?: boolean }) => {
  const { recalculateOffers: recalculate, calculatingOffersIds } = useGetOffersContext()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const isRecalculating = calculatingOffersIds?.includes(offer!.offerId) ?? false


  const { isLoading: isSaving, mutateAsync: saveDiscounts } = useSaveOfferDiscounts(offer.offerId)

  const methods = useForm<OfferDiscounts>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      discounts: []
    }
  })
  const { handleSubmit, setError, reset, formState: { errors }, setValue, watch, getValues } = methods

  const { refetch, data, isLoading, isFetching, isFetched } = useGetOfferAvailableDiscounts(offer.offerId, {
    enabled: false,
    onSuccess: ({ data }) => {
      setValue('discounts', data.filter(x => x.editK2 || x.showK2) ?? [])
    }
  })

  useEffect(() => {
    setTimeout(() => {
      refetch() // at nebrani nacteni jinym dulezitejsim requestum
    }, 3000)
  }, [])

  useEffect(() => {
    if (!isRecalculating) {
      refetch() // slevy se mohou po rekalkulaci zmenit      
    }
  }, [isRecalculating])

  const ds = data?.data.filter(x => x.editK2 || x.showK2) ?? []

  const submitForm = handleSubmit((data) => {
    setIsSubmitting(true)
    saveDiscounts(data)
      .then((resp) => {
        recalculate([offer.offerId], true)
      })
      .catch((err) => {
        if (!(err.response && err.response.status === 400 && err.response.data))
          return

        const data = err.response.data
        // zobrazeni serverove validace
        Object.keys(data).forEach((x: any) => {
          setError(global.toCamelCase(x), { message: data[x].join(' ') })
        })
      })
      .finally(() => {
        setIsSubmitting(false)
      })
  })


  return (
    <OfferCellWrapper sx={{ justifyContent: 'end', justifyItems: 'end', mr: isDisabled ? 5 : 0 }}>
      {(isLoading || !isFetched) && <Spinner />}

      {!isLoading && ds.length > 0 &&
        ds.map((x, i) => {
          const discount = watch(`discounts.${i}.discount`)

          return (
            <EditablePriceCustom
              key={x.insuranceCategoryDiscountId}
              value={discount?.toString()}
              text={`${discount ?? '???'}${x.unit}`}
              min={x.minDiscount}
              max={x.maxDiscount}
              unit={x.unit}
              errorMessage={errors?.discounts ? errors.discounts[i]?.discount?.message : undefined}
              isDisabled={!x.editK2 || x.minDiscount == x.maxDiscount || isDisabled}
              tooltip={x.name}
              onSubmit={(val) => {
                if (val != getValues(`discounts.${i}.discount`)) {
                  setValue(`discounts.${i}.discount`, val)
                  submitForm()
                }
              }} />
          )
        })}

      {!isLoading && isFetched && ds.length == 0 &&
        <Text sx={{ p: 0, fontSize: 'sm', mr: !isDisabled ? 5 : 0 }}>
          0%
        </Text>}
    </OfferCellWrapper>
  )
}
